import React, { useState } from 'react';
import {
  CardContents,
  TitleContainer,
  CardTitle,
  CancelButton,
  FormError,
  InputContainer,
  AmountInput,
  PrimaryButton,
  ButtonContainer,
  SecondaryButton,
} from 'common/components/cardStyles';
import Icon from '@mdi/react';
import { mdiClose } from '@mdi/js';
import { useProducerWithThunks } from 'common/hooks/useProducer';

import { defaultAPIRequestState } from 'api';
import { Wallet } from 'common/state/banking/actions';
import { updateWalletThresholdRecipe } from 'common/state/banking/recipes';
import { doUpdateWalletThreshold } from 'common/state/banking/thunkActions';

interface ThresholdModal {
  wallet: Wallet;
  setShouldRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  closeModal: () => void;
}

export function ThresholdModal({
  wallet,
  setShouldRefresh,
  closeModal,
}: ThresholdModal) {
  const [threshold, setThreshold] = useState('');

  const clearTransaction: React.MouseEventHandler = React.useCallback(() => {
    setThreshold('');
  }, [setThreshold]);

  const [state, dispatch] = useProducerWithThunks(
    updateWalletThresholdRecipe,
    defaultAPIRequestState
  );

  const submit: React.FormEventHandler = React.useCallback(
    (e) => {
      e.preventDefault();
      dispatch(
        doUpdateWalletThreshold(wallet.id, {
          settlement_threshold: parseFloat(threshold),
        })
      );
    },
    [dispatch, threshold, wallet]
  );

  // Close the modal if the request succeeded
  const { success } = state;
  React.useEffect(() => {
    if (success) {
      closeModal();
      setShouldRefresh(true);
    }
  }, [closeModal, setShouldRefresh, success]);

  return (
    <form onSubmit={submit}>
      <CardContents>
        <TitleContainer>
          <CardTitle>Update Wallet Threshold: {wallet.title}</CardTitle>
          <CancelButton type="button" onClick={closeModal}>
            <Icon size={0.8} path={mdiClose} />
          </CancelButton>
        </TitleContainer>
        <TitleContainer>
          {state.error && <FormError>{state.error}</FormError>}
        </TitleContainer>
        <InputContainer>
          <AmountInput
            currency={wallet?.currency || 'NONE'}
            shrinked
            label="Threshold *"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
          />
        </InputContainer>
        <ButtonContainer>
          <PrimaryButton type="submit" disabled={!threshold}>
            Set
          </PrimaryButton>
          <SecondaryButton type="button" onClick={clearTransaction}>
            Clear
          </SecondaryButton>
        </ButtonContainer>
      </CardContents>
    </form>
  );
}
